import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { motion } from "framer-motion";
import DataTable from "../../components/DataTable";
import { useSnackbar } from "../../context/SnackbarContext";
import { reportService } from "../../services/reports/reportService";
import { Report, ReportParameter } from "../../types/api.types";

const PAGE_SIZE = 8;

function compareReportCellValues(a: unknown, b: unknown): number {
  if (a === b) return 0;
  const aEmpty = a == null || a === "";
  const bEmpty = b == null || b === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  if (typeof a === "number" && typeof b === "number") {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  }

  if (a instanceof Date && b instanceof Date) {
    return a.getTime() - b.getTime();
  }

  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

interface DataTableColumn {
  header: string;
  key: string;
}

const ReportsPage = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [reportResult, setReportResult] = useState<{
    columns: DataTableColumn[];
    data: Record<string, unknown>[];
  } | null>(null);
  const [paramErrors, setParamErrors] = useState<Set<string>>(new Set());
  const [reportPage, setReportPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const { showSnackbar } = useSnackbar();
  const theme = useTheme();

  useEffect(() => {
    const fetchReports = async () => {
      setIsLoading(true);
      try {
        const data = await reportService.getReports();
        setReports(data);
      } finally {
        setIsLoading(false);
      }
    };
    fetchReports();
  }, []);

  useEffect(() => {
    if (selectedReport) {
      const initial: Record<string, string> = {};
      selectedReport.parameters.forEach((p) => {
        initial[p.name] = "";
      });
      setFilterValues(initial);
    } else {
      setFilterValues({});
    }
  }, [selectedReport]);

  useEffect(() => {
    setReportPage(1);
    setSortColumn(null);
    setSortDirection("asc");
  }, [reportResult]);

  const handleReportSortChange = (key: string) => {
    if (key === "") {
      setSortColumn(null);
      setSortDirection("asc");
      return;
    }
    if (sortColumn === key) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(key);
    setSortDirection("asc");
    setReportPage(1);
  };

  const handleCardClick = (report: Report) => {
    setSelectedReport(report);
    setReportPage(1);
    setReportResult(null);
    setParamErrors(new Set());
    setFilterValues({});
  };

  const handleFilterChange = (paramName: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [paramName]: value }));
  };

  const handleGenerate = async () => {
    if (!selectedReport) return;

    const missingRequired = selectedReport.parameters.filter((p) => {
      if (!p.required) return false;
      const v = filterValues[p.name];
      return v == null || String(v).trim() === "";
    });

    if (missingRequired.length > 0) {
      setParamErrors(new Set(missingRequired.map((p) => p.name)));
      const labels = missingRequired.map((p) => p.label || p.name).join(", ");
      showSnackbar(`Completa los campos requeridos: ${labels}`, "error", 5000);
      return;
    }

    setParamErrors(new Set());
    const parameters: Record<string, string> = {};
    selectedReport.parameters.forEach((p) => {
      const value = filterValues[p.name];
      if (value != null && value !== "") {
        parameters[p.name] = value;
      }
    });
    setIsExecuting(true);
    setReportResult(null);
    try {
      const response = await reportService.executeReport(
        selectedReport.reportId,
        parameters,
      );
      const columns: DataTableColumn[] = response.columns.map((col) => ({
        header: col,
        key: col,
      }));
      const data = response.rows.map((row) => {
        const obj: Record<string, unknown> = {};
        response.columns.forEach((colKey) => {
          const value = Array.isArray(row)
            ? row[response.columns.indexOf(colKey)]
            : (row as Record<string, unknown>)[colKey];
          obj[colKey] = value;
        });
        return obj;
      });
      setReportResult({ columns, data });
      showSnackbar("Reporte generado correctamente", "success", 3000);
    } catch {
      showSnackbar("Error al generar el reporte", "error", 5000);
    } finally {
      setIsExecuting(false);
    }
  };

  const handleExportExcel = async () => {
    if (!selectedReport) return;
    const parameters: Record<string, string> = {};
    selectedReport.parameters.forEach((p) => {
      const value = filterValues[p.name];
      if (value != null && value !== "") {
        parameters[p.name] = value;
      }
    });
    setIsExporting(true);
    try {
      const blob = await reportService.exportReport(
        selectedReport.reportId,
        parameters,
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte-${selectedReport.reportId}-${Date.now()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } finally {
      setIsExporting(false);
    }
  };

  const renderParameterInput = (param: ReportParameter) => {
    const value = filterValues[param.name] ?? "";
    const hasError = param.required && paramErrors.has(param.name);
    const helperText = hasError ? "Campo requerido" : undefined;
    if (param.type === "date") {
      return (
        <TextField
          key={param.name}
          label={param.label}
          type="date"
          value={value}
          onChange={(e) => {
            handleFilterChange(param.name, e.target.value);
            if (paramErrors.has(param.name)) {
              setParamErrors((prev) => {
                const next = new Set(prev);
                next.delete(param.name);
                return next;
              });
            }
          }}
          required={param.required}
          size="small"
          error={hasError}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 180 }}
        />
      );
    }
    return (
      <TextField
        key={param.name}
        label={param.label}
        type="text"
        value={value}
        onChange={(e) => {
          handleFilterChange(param.name, e.target.value);
          if (paramErrors.has(param.name)) {
            setParamErrors((prev) => {
              const next = new Set(prev);
              next.delete(param.name);
              return next;
            });
          }
        }}
        required={param.required}
        size="small"
        error={hasError}
        helperText={helperText}
        sx={{ minWidth: 200 }}
      />
    );
  };

  const reportResultMemo = useMemo(() => reportResult, [reportResult]);

  const tableColumns = reportResult?.columns ?? [
    { header: "Columna 1", key: "col1" },
    { header: "Columna 2", key: "col2" },
    { header: "Columna 3", key: "col3" },
  ];

  const tableData = reportResultMemo?.data ?? [];
  const totalItems = tableData.length;

  const sortedTableData = useMemo(() => {
    if (!sortColumn || tableData.length === 0) return tableData;
    const dir = sortDirection === "asc" ? 1 : -1;
    return [...tableData].sort(
      (rowA, rowB) =>
        compareReportCellValues(rowA[sortColumn], rowB[sortColumn]) * dir,
    );
  }, [tableData, sortColumn, sortDirection]);

  const paginatedData = sortedTableData.slice(
    (reportPage - 1) * PAGE_SIZE,
    reportPage * PAGE_SIZE,
  );

  return (
    <Box>
      <Card>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{ marginBottom: 16 }}
          >
            <Typography variant="h4">Reportes</Typography>
          </motion.div>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Selecciona un reporte
          </Typography>
          <Box
            sx={{
              maxHeight: 500,
              overflow: "auto",
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              p: 2,
              mb: 3,
            }}
          >
            {isLoading ? (
              <Typography>Cargando reportes...</Typography>
            ) : (
              <Grid container spacing={2}>
                {reports.map((report) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={report.reportId}>
                    <Card
                      onClick={() => handleCardClick(report)}
                      sx={{
                        cursor: "pointer",
                        border:
                          selectedReport?.reportId === report.reportId
                            ? `2px solid ${theme.palette.primary.main}`
                            : "1px solid",
                        borderColor:
                          selectedReport?.reportId === report.reportId
                            ? "primary.main"
                            : "divider",
                        "&:hover": {
                          borderColor: "primary.light",
                          bgcolor: "action.hover",
                        },
                      }}
                    >
                      <CardContent>
                        <Typography variant="h6">{report.name}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            {selectedReport && selectedReport.parameters.length > 0 && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Filtros
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    {selectedReport.parameters.map((param) =>
                      renderParameterInput(param),
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={isExecuting}
                      startIcon={
                        isExecuting ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : null
                      }
                      onClick={handleGenerate}
                    >
                      {isExecuting ? "Generando..." : "Generar"}
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={!selectedReport || isExporting}
                    startIcon={
                      isExporting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : null
                    }
                    onClick={handleExportExcel}
                  >
                    {isExporting ? "Descargando..." : "Descargar como excel"}
                  </Button>
                </Box>
              </>
            )}
          </Box>

          {paginatedData.length > 0 && (
            <DataTable
              isLoading={isExecuting}
              columns={tableColumns}
              total={totalItems}
              data={paginatedData}
              page={reportPage}
              onChange={(page) => setReportPage(page)}
              rowsPerPage={PAGE_SIZE}
              horizontalScroll
              sortable
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSortChange={handleReportSortChange}
            />
          )}

          {paginatedData.length === 0 && selectedReport && (
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ textAlign: "center", mt: 5 }}
            >
              No hay datos para mostrar
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ReportsPage;
